import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canManageOwnedResource } from '../utils/resourcePermissions';
import PageLayout from '../components/layout/PageLayout';
import PostDetailView from '../components/posts/PostDetailView';
import PostCard from '../components/posts/PostCard';
import postService from '../services/postService';
import { formatDate } from '../utils/helpers';
import { parseSocietyUrl, getSlugFromParams, getPostUrl } from '../utils/urlHelpers';

const PostDetailPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [societySlug, setSocietySlug] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  // Get the post identifier (slug or ID)
  const postIdentifier = getSlugFromParams(params, 'post');
  
  // Check if this is a society-specific URL
  const { isSocietySpecific, societySlug: urlSocietySlug } = parseSocietyUrl(location.pathname);

  useEffect(() => {
    if (postIdentifier) {
      setSocietySlug(urlSocietySlug);
      loadPost();
    }
  }, [postIdentifier, urlSocietySlug]);

  // Load related posts after post is loaded
  useEffect(() => {
    if (post && post.category) {
      loadRelatedPosts();
    }
  }, [post]);

  // Load comments when post is loaded (post detail API does not include comments list)
  useEffect(() => {
    if (post && post.id) {
      loadComments();
    }
  }, [post?.id]);

  const loadPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const postData = await postService.getPost(postIdentifier);
      setPost(postData);
    } catch (err) {
      console.error('Error loading post:', err);
      setError('Failed to load post details');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedPosts = async () => {
    if (!post || !post.category) return;
    
    try {
      const response = await postService.getPosts({ 
        category: post.category,
        page_size: 4,
        exclude: postIdentifier 
      });
      // Limit to 4 posts; exclude current post (defensive in case API returns it)
      const posts = response.results || (Array.isArray(response) ? response : []);
      const currentId = post.id;
      const currentSlug = post.slug;
      const filtered = posts.filter(
        (p) => p.id !== currentId && p.slug !== currentSlug
      );
      setRelatedPosts(filtered.slice(0, 4));
    } catch (err) {
      console.error('Error loading related posts:', err);
      setRelatedPosts([]);
    }
  };

  const loadComments = async () => {
    if (!post?.id) return;
    setCommentsLoading(true);
    try {
      const data = await postService.getComments(post.id);
      const list = data?.results ?? (Array.isArray(data) ? data : data?.data ?? []);
      setComments(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading comments:', err);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleEdit = () => {
    if (societySlug) {
      navigate(`/societies/${societySlug}/posts/${postIdentifier}/edit`);
    } else {
      navigate(`/posts/${postIdentifier}/edit`);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await postService.deletePost(postIdentifier);
        if (societySlug) {
          navigate(`/societies/${societySlug}/posts`);
        } else {
          navigate('/posts');
        }
      } catch (err) {
        console.error('Error deleting post:', err);
        alert('Failed to delete post');
      }
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate('/?auth=login');
      return;
    }

    try {
      await postService.toggleLike(post.id);
      // Refresh post data to get updated like count
      loadPost();
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleShare = async () => {
    if (!isAuthenticated) {
      navigate('/?auth=login');
      return;
    }

    try {
      await postService.sharePost(post.id);
      alert('Post shared successfully!');
    } catch (err) {
      console.error('Error sharing post:', err);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/?auth=login');
      return;
    }

    if (!newComment.trim()) return;

    try {
      setSendingComment(true);
      await postService.addComment(post.id, { content: newComment });
      setNewComment('');
      setShowCommentForm(false);
      // Refresh post data (comment count) and comments list
      loadPost();
      loadComments();
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      news: 'bg-blue-100 text-blue-800',
      technology: 'bg-purple-100 text-purple-800',
      business: 'bg-green-100 text-green-800',
      health: 'bg-red-100 text-red-800',
      lifestyle: 'bg-pink-100 text-pink-800',
      entertainment: 'bg-yellow-100 text-yellow-800',
      sports: 'bg-orange-100 text-orange-800',
      education: 'bg-indigo-100 text-indigo-800',
      opinion: 'bg-teal-100 text-teal-800',
      qa: 'bg-cyan-100 text-cyan-800',
      travel: 'bg-emerald-100 text-emerald-800',
      visa: 'bg-sky-100 text-sky-800',
      recipes: 'bg-amber-100 text-amber-800',
      announcements: 'bg-violet-100 text-violet-800',
      local: 'bg-lime-100 text-lime-800',
      deals: 'bg-rose-100 text-rose-800',
      other: 'bg-slate-100 text-slate-800',
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <PageLayout title="Loading Post">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading post...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Error">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Post</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!post) {
    return (
      <PageLayout title="Post Not Found">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">📝</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Post Not Found</h1>
            <p className="text-gray-600 mb-4">The post you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const canManage = isAuthenticated && user && post && canManageOwnedResource(user, post);

  return (
    <PageLayout title={`${post.title} - PakPoints`}>
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {/* Top Ad - Removed to avoid header confusion */}

          <div className="grid grid-cols-1 gap-8">
            {/* Main Content */}
            <div className="w-full">
              <PostDetailView
                post={post}
                comments={comments}
                commentsLoading={commentsLoading}
                isOwner={canManage}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onLike={handleLike}
                onShare={handleShare}
                onSubmitComment={handleCommentSubmit}
                showCommentForm={showCommentForm}
                setShowCommentForm={setShowCommentForm}
                newComment={newComment}
                setNewComment={setNewComment}
                sendingComment={sendingComment}
              />
            </div>

            {/* Related Posts Section */}
            {relatedPosts.length > 0 && (
              <div className="w-full mt-8">
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">Related Posts</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {relatedPosts.map((relatedPost) => (
                      <PostCard
                        key={relatedPost.id}
                        post={relatedPost}
                        onClick={() => navigate(getPostUrl(relatedPost, societySlug))}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Ad - Removed to avoid content confusion */}
        </div>
      </div>
    </PageLayout>
  );
};

export default PostDetailPage;
